import React from "react";

/**
 * Helper to parse inline markdown:
 * - **bold**
 * - *italic*
 * - `code`
 * - [text](url)
 */
const parseInlineMarkdown = (text: string): React.ReactNode[] => {
  if (!text) return [];

  // Match **bold**, *italic*, `code`, [text](url)
  const regex = /(\*\*(.*?)\*\*|\*(.*?)\*|`(.*?)`|\[(.*?)\]\((.*?)\))/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    // Push preceding plain text
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    const [, , boldText, italicText, codeText, linkText, linkUrl] = match;

    if (boldText !== undefined) {
      result.push(<strong key={match.index}>{boldText}</strong>);
    } else if (italicText !== undefined) {
      result.push(<em key={match.index}>{italicText}</em>);
    } else if (codeText !== undefined) {
      result.push(
        <code
          key={match.index}
          style={{
            background: "rgba(255, 255, 255, 0.12)",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "0.9em",
            fontFamily: "monospace",
          }}
        >
          {codeText}
        </code>,
      );
    } else if (linkText !== undefined && linkUrl !== undefined) {
      result.push(
        <a
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1890ff", textDecoration: "underline" }}
        >
          {linkText}
        </a>,
      );
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
};

interface FormattedMarkdownProps {
  content: string;
}

type ListItem = {
  indent: number;
  node: React.ReactNode[];
};

type ListGroup = {
  type: "ol" | "ul";
  items: ListItem[];
};

export const FormattedMarkdown: React.FC<FormattedMarkdownProps> = ({
  content,
}) => {
  if (!content) return null;

  // Normalize newline escapes
  const normalized = content
    .replace(/\\n/g, "\n")
    .replace(/(?<!\w)\/n(?!\w)/g, "\n");

  const lines = normalized.split("\n");
  const elements: React.ReactNode[] = [];
  let currentList: ListGroup | null = null;

  const flushList = () => {
    if (!currentList) return;
    const ListTag = currentList.type;
    const listKey = `list-${elements.length}`;

    elements.push(
      <ListTag
        key={listKey}
        style={{
          margin: "4px 0 8px 0",
          paddingLeft: "22px",
          listStylePosition: "outside",
        }}
      >
        {currentList.items.map((item, idx) => (
          <li
            key={idx}
            style={{
              marginLeft: item.indent > 0 ? `${item.indent * 16}px` : undefined,
              marginBottom: "4px",
              lineHeight: "1.5",
            }}
          >
            {item.node}
          </li>
        ))}
      </ListTag>,
    );
    currentList = null;
  };

  lines.forEach((line, lineIdx) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      elements.push(<div key={`empty-${lineIdx}`} style={{ height: "4px" }} />);
      return;
    }

    // Count indentation spaces to handle sub-lists
    const indentMatch = line.match(/^(\s*)/);
    const indentSpaces = indentMatch ? indentMatch[1].length : 0;
    const indentLevel = Math.floor(indentSpaces / 2);

    // Check for ordered list item (e.g. "1. ", "2. ")
    const olMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (olMatch) {
      const itemText = olMatch[2];
      if (currentList && currentList.type !== "ol") {
        flushList();
      }
      if (!currentList) {
        currentList = { type: "ol", items: [] };
      }
      currentList.items.push({
        indent: indentLevel,
        node: parseInlineMarkdown(itemText),
      });
      return;
    }

    // Check for bullet list item (e.g. "* ", "- ", "+ ")
    const ulMatch = trimmed.match(/^[*+-]\s+(.*)$/);
    if (ulMatch) {
      const itemText = ulMatch[1];
      if (currentList && currentList.type !== "ul") {
        flushList();
      }
      if (!currentList) {
        currentList = { type: "ul", items: [] };
      }
      currentList.items.push({
        indent: indentLevel,
        node: parseInlineMarkdown(itemText),
      });
      return;
    }

    // Line is not a list item -> flush open list
    flushList();

    // Headers
    if (trimmed.startsWith("### ")) {
      elements.push(
        <h4
          key={`h4-${lineIdx}`}
          style={{ margin: "8px 0 4px 0", fontWeight: 700 }}
        >
          {parseInlineMarkdown(trimmed.slice(4))}
        </h4>,
      );
    } else if (trimmed.startsWith("## ")) {
      elements.push(
        <h3
          key={`h3-${lineIdx}`}
          style={{ margin: "10px 0 4px 0", fontWeight: 700 }}
        >
          {parseInlineMarkdown(trimmed.slice(3))}
        </h3>,
      );
    } else if (trimmed.startsWith("# ")) {
      elements.push(
        <h2
          key={`h2-${lineIdx}`}
          style={{ margin: "12px 0 6px 0", fontWeight: 700 }}
        >
          {parseInlineMarkdown(trimmed.slice(2))}
        </h2>,
      );
    } else if (trimmed.startsWith("> ")) {
      elements.push(
        <blockquote
          key={`bq-${lineIdx}`}
          style={{
            borderLeft: "3px solid #1890ff",
            paddingLeft: "10px",
            margin: "6px 0",
            opacity: 0.85,
            fontStyle: "italic",
          }}
        >
          {parseInlineMarkdown(trimmed.slice(2))}
        </blockquote>,
      );
    } else {
      elements.push(
        <div
          key={`p-${lineIdx}`}
          style={{ marginBottom: "2px", lineHeight: "1.5" }}
        >
          {parseInlineMarkdown(line)}
        </div>,
      );
    }
  });

  flushList();

  return <div className="formatted-markdown-wrapper">{elements}</div>;
};

export default FormattedMarkdown;
